import { jobPositionText } from './job-crew-positions';

/**
 * Human-readable labels for enum values that appear in the UI.
 * Used by both the internal ticket detail and the public customer review page.
 */

// ── Line item types ──────────────────────────────────────────────────────────

/**
 * Line item type labels live in `src/constants/line-item-types.ts` — the
 * canonical mirror of the backend enum. This re-export keeps the existing
 * `fmtLineItemType` call sites working.
 */
export { getLineItemTypeLabel as fmtLineItemType } from '../constants/line-item-types';

// ── Crew role on job ─────────────────────────────────────────────────────────

/**
 * `job_crew_assignments.role_on_job` USED to do double duty: free-text display
 * copy for a crew member's role on a job ("Tong Operator", "Swamper"), AND the
 * carrier for the sentinel below, which the API's FIELD_TICKET_WRITE_PREDICATE
 * read to decide who may write that job's field tickets (fieldiq-api #188).
 *
 * API migration 0342 moved the designation to its own column,
 * `job_crew_assignments.is_ticket_writer`, and cleared the sentinel from every
 * row. So `role_on_job` is a label and nothing else, and after the migration no
 * payload can carry this string.
 *
 * ─── WHY THE FILTER BELOW STAYS ─────────────────────────────────────────────
 *
 * Deploy ordering. The migration is applied by hand, and a bundle already in a
 * phone's cache can be talking to a pre-0342 API for as long as that takes. In
 * that window a crew payload still carries the sentinel, and rendered as-is it
 * tells the crew a hand's trade is "ticket_writer" — and on JobDetail splits
 * him into his own crew group heading.
 *
 * Delete the constant and the filter once the API has been on 0342 long enough
 * that no cached bundle can reach the old one. It is a shim with an expiry, not
 * part of the model. `lib/field-ticket-write.ts` carries the same note.
 *
 * The `isTicketWriter(member)` helper that used to live here is GONE. It read
 * the sentinel, so 0342 made it answer "no" for everybody — and it had no
 * callers outside its own test, which is how it would have stayed wrong
 * unnoticed. The live question is answered by `isDesignatedTicketWriter` in
 * lib/field-ticket-write.ts, which reads the boolean. One question, one
 * implementation.
 */
export const LEGACY_TICKET_WRITER_ROLE = 'ticket_writer';

interface CrewRoleFields {
  role_on_job?: string | null;
  position_name?: string | null;
  /** The hand's position ON THIS JOB (API 0426), either casing. */
  positions?: unknown;
}

/**
 * The crew member's role as display copy. Precedence:
 *
 *   1. the job position (0426) — per phase when split: "Wash Truck Swamper
 *      (wash) · Tong Hand (run)"; see `jobPositionText`
 *   2. `role_on_job`, with the retired ticket-writer sentinel filtered out so it
 *      can never surface as a job title
 *   3. `position_name` — the hand's PRIMARY position, what they usually do
 *   4. `fallback`
 *
 * The job position leads because it is the only one of the four that answers
 * "what is this hand doing on THIS job"; the primary is a fact about the hand.
 */
export function crewRoleLabel(
  member: CrewRoleFields | null | undefined,
  fallback: string = 'Crew',
): string {
  const jobPosition = jobPositionText(member?.positions);
  const role = member?.role_on_job;
  const realRole = role && role !== LEGACY_TICKET_WRITER_ROLE ? role : null;
  return jobPosition || realRole || member?.position_name || fallback;
}

// ── Billing unit ─────────────────────────────────────────────────────────────

const UNIT_LABELS: Record<string, string> = {
  per_metre: 'Per Metre',
  per_foot: 'Per Foot',
  per_hour: 'Per Hour',
  per_day: 'Per Day',
  flat_fee: 'Flat Fee',
  per_job: 'Per Job',
  each: 'Each',
  per_joint: 'Per Joint',
  // Directional & MWD crew lines on a downhole quote (api#1092): the unit names
  // which of a role's three day rates the line priced.
  standby_day: 'Per Standby Day',
  travel_day: 'Per Travel Day',
};

/** Convert a billing unit enum to a human label. */
export function fmtUnit(unit: string): string {
  if (!unit) return '';
  return UNIT_LABELS[unit] || unit.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ── Number formatting ────────────────────────────────────────────────────────

/**
 * Format a quantity: no trailing zeros, thousands separators.
 * 6544.0000 → 6,544; 6544.5 → 6,544.5
 *
 * ABSENT IS NOT ZERO — same contract as fmtCurrency below. null, undefined and
 * '' render '—'; a real 0 renders '0'.
 *
 * `Number.isFinite` alone was NOT enough, which is what this function got wrong
 * for as long as it existed. Number(null) and Number('') are both the number 0,
 * and Number.isFinite(0) is true, so those two sailed past the guard and
 * rendered '0'. Only Number(undefined) is NaN, so undefined was the single
 * absent case the old code happened to get right. The absent cases have to be
 * rejected BEFORE the coercion, by identity, not after it by finiteness.
 *
 * This mattered more than a display nit: the `no-absent-as-zero` lint rule cited
 * this function as the CORRECT model to copy, so the defect was being taught.
 * A quantity here is billable —
 * metres run, joints, hours — so an absent one rendered as '0' is an under-bill
 * that reads as a real measurement nobody will question.
 */
// `maxDigits` is a display choice, like fmtHours' `digits`: Safety Metrics'
// window total reads as whole hours ("1,461 hrs worked").
export function fmtQty(v: number | string | undefined | null, maxDigits: number = 4): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  // -0 formats as "-0", which is not a quantity. (-0 === 0.) fmtCurrency below
  // already does this; fmtQty never did.
  return (n === 0 ? 0 : n).toLocaleString('en-US', { maximumFractionDigits: maxDigits });
}

/**
 * A file size as a person reads it: '184 KB', '2.4 MB'.
 *
 * Same contract as fmtQty above — null, undefined, '' and anything non-finite
 * render '—', rejected BY IDENTITY before the coercion, because Number(null)
 * and Number('') are both 0 and would sail past a finiteness check as '0 B'.
 * A genuinely empty file IS '0 B' and must stay distinguishable from one whose
 * size nobody knows; that distinction is the whole reason this is not a
 * three-line expression at the call site.
 *
 * Powers of 1024, matching how the API counts its 20 MB cap
 * (`STAMPED_FILE_MAX_BYTES = 20 * 1024 * 1024`). A decimal-megabyte helper
 * would tell a consultant their 20.6 MB photo is "19.6 MB" and under the limit,
 * and then the server would refuse it.
 */
export function fmtFileSize(v: number | string | undefined | null): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${Math.round(n)} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * Format a span of hours: fixed decimals, absent renders '—'.
 *
 * Same contract as fmtQty/fmtCurrency — null, undefined and '' are rejected BY
 * IDENTITY before the coercion, because Number(null) and Number('') are both 0
 * and would sail past a finiteness check. A real 0 renders '0.00'.
 *
 * Hours are the highest-stakes number in this app after money: payroll screens
 * showed `(Number(hrs) || 0).toFixed(1)`, so a hand whose hours the server could
 * not score was told they worked 0.0 — on a screen that decides what they get
 * paid. That is the whole reason this rejects before coercing.
 *
 * `digits` exists because payroll tables were built on one decimal and tickets
 * on two; it is a display choice, not a semantic one.
 */
export function fmtHours(
  v: number | string | undefined | null,
  digits: number = 2,
): string {
  return fmtFixed(v, digits);
}

/**
 * Fixed decimals, absent renders '—'. The shared body behind fmtHours and
 * fmtRate.
 *
 * Extracted rather than left duplicated: the two call sites differ only in what
 * the number MEANS, and the contract — reject null/undefined/'' by identity
 * before coercing, because Number(null) and Number('') are both 0 — is the part
 * that must not drift. Two copies of it is how one of them grows a `|| 0`.
 */
function fmtFixed(v: number | string | undefined | null, digits: number): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return (n === 0 ? 0 : n).toFixed(digits);
}

/**
 * Format a safety rate: fixed decimals, absent renders '—'.
 *
 * The absence case is the whole reason this is not `value.toFixed(2)` at the
 * call site. A safety rate that cannot be computed comes back null, and a null
 * TRIF rendered as "0.00" claims the company worked real hours and hurt nobody
 * — on a figure that goes on prequalification packages. A REAL 0 still renders
 * "0.00", and telling those two apart is the point.
 *
 * @see lib/safety-metrics.ts describeRate, which decides which case applies.
 */
export function fmtRate(
  v: number | string | undefined | null,
  digits: number = 2,
): string {
  return fmtFixed(v, digits);
}

/**
 * A tax rate as a percentage: '5.00' → '5%', a stated zero → '0%', absent → '—'.
 *
 * NOT `fmtPayPercentage`, which is the sibling above and answers a different
 * question in two ways that would both be wrong here:
 *
 *   - it takes a FRACTION (0.35 → '35%'); a tax rate is already a percentage
 *     (`company_tax_rates.tax_rate_1` is NUMERIC(6,3) holding 5.000 for 5%)
 *   - it treats 0 as absent, because a 0% pay percentage is a pay type that
 *     has none. A 0.00 tax rate is a tenant SAYING the rate is zero, and five
 *     of the twelve seeded production rows say exactly that while six others
 *     hold NULL. Collapsing those two is the silent-blank-field family.
 *
 * Trailing zeros are dropped because the column pads them: '5.000' reads better
 * as 5% in a table column, and '13.500' as 13.5%. THREE places, not two:
 * Quebec's QST is 9.975%, and two places would print 9.98% — a rate the
 * province does not levy (API 0483 widened the column for the same reason).
 */
export function fmtTaxRate(v: number | string | undefined | null): string {
  const n = parseMoney(v);
  if (n === null) return '—';
  return `${Number(n.toFixed(3))}%`;
}

/**
 * Coerce a server money value to a number, preserving absence.
 *
 * The counterpart to fmtCurrency: use it where the old code said
 * `parseFloat(x) || 0`. NULL, undefined, '' and unparseable text all become
 * null so that fmtCurrency can render '—'; a real 0 stays 0.
 */
export function parseMoney(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Total a column of money/hours, preserving absence across the whole sum.
 *
 * Returns null the moment ANY contributor is absent, because a total that
 * silently skipped a row is the same lie as a single absent value rendered as
 * zero — worse, actually, since a column total is the number people act on and
 * there is nothing on screen to say a row was dropped. `reduce((s, r) => s +
 * (Number(r.gross_pay) || 0), 0)` over ten workers, one of whom the server
 * could not score, produces a confident payroll figure that is short by one
 * person's pay.
 *
 * A real 0 contributes 0 and keeps the total a number. An empty list totals 0,
 * which is correct: nothing to pay is not the same as unknown.
 */
export function sumMoney(values: readonly unknown[]): number | null {
  let total = 0;
  for (const v of values) {
    const n = parseMoney(v);
    if (n === null) return null;
    total += n;
  }
  return total;
}

/**
 * Format a currency amount: `$` + 2 decimals + thousands separators.
 *
 * ABSENT IS NOT ZERO. null, undefined and '' render '—', because a ticket whose
 * subtotal the server could not compute is not a ticket worth $0.00 — and a
 * zero that is really a missing number is indistinguishable from a real one, so
 * nobody ever files a bug for it. A genuine 0 still renders '$0.00'.
 *
 * `Number.isFinite` alone is not enough: Number(null) and Number('') are both 0,
 * so the absent cases have to be rejected BEFORE the coercion. (fmtQty above had
 * that same gap until PR #302 — it returned '0' for null where it meant '—'.)
 *
 * This function owns the symbol. Call sites must not prefix their own, or an
 * absent value renders '$—'. Where the amount's currency matters, pass it as the
 * second argument — 'CAD' gives 'CA$', 'USD' gives 'US$', anything else (or
 * nothing) gives a bare '$'. The prefix is applied only to a value that is
 * actually there; absent stays a bare '—', never 'CA$—'.
 *
 * (The `currency` argument is the one thing EmployeeProfile's local copy of this
 * function did that the shared one could not, which is why it is here rather
 * than duplicated there.)
 *
 * `digits` is the second such thing, and it is why nine report screens each
 * carried their own `fmt`. A revenue roll-up renders whole dollars — "$1,284,113"
 * — because cents on a seven-figure total are noise; an invoice line renders
 * "$1,284,113.00" because cents are the point. Two decimals was hard-coded here,
 * so a screen that wanted whole dollars had no way to ask, and wrote its own
 * function instead. Every one of those local copies then re-decided the absent
 * case independently, and most of them decided it wrong. The display choice was
 * never the disagreement worth forking a formatter over — this argument is the
 * whole of it, and it is a display choice, not a semantic one: `digits` changes
 * how many decimals you see, never whether an absent value becomes a zero.
 *
 * `unit` is the third, and it follows the prefix's rule: it is appended only to
 * an amount that is there. A rate reads "$450.00/day"; an absent rate reads a
 * bare '—', never '—/day'. Before this argument every rate site had to choose
 * between gluing the suffix onto a possible em dash and hand-formatting the
 * number itself — and the hand-formatted copies each re-decided the absent case
 * (`Number('')` is 0, so a blank rate printed "$0.00/hr").
 */
export function fmtCurrency(
  v: number | string | undefined | null,
  currency?: string | null,
  digits: number = 2,
  unit?: string,
): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const symbol = currency === 'CAD' ? 'CA$' : currency === 'USD' ? 'US$' : '$';
  // -0 formats as "-0.00", which is not a thing on an invoice. (-0 === 0.)
  const amount = n === 0 ? 0 : n;
  // The sign stays inside the '$' — "$-42.00" — because that is what the call
  // sites rendered when each of them prefixed its own '$', and a credit line
  // changing shape is not part of this fix.
  return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}${unit ?? ''}`;
}

/**
 * How many decimals a unit RATE needs: 2 when it is a whole number of cents,
 * otherwise 3 or 4 — whatever the value actually carries.
 *
 * Quote and invoice line rates come back from the API as numeric(12,4) strings
 * (api 0466): '150.0000' for every existing rate, '12.3456' for a genuinely
 * sub-cent one. A whole-cent rate must keep rendering exactly as before
 * ('$150.00'); a sub-cent one must not be rounded to two, or the rate on the
 * screen does not multiply out to the line's amount.
 */
export function rateDigits(v: number | string | undefined | null): number {
  const n = Number(v);
  if (v === null || v === undefined || v === '' || !Number.isFinite(n)) return 2;
  if (Math.abs(n * 100 - Math.round(n * 100)) < 1e-6) return 2;
  return Math.abs(n * 1000 - Math.round(n * 1000)) < 1e-6 ? 3 : 4;
}

/** A unit rate: `$150.00`, `$12.3456`, `CA$-0.0784`; absent is `—`, as fmtCurrency. */
export function fmtUnitRate(v: number | string | undefined | null, currency?: string | null): string {
  return fmtCurrency(v, currency, rateDigits(v));
}

/**
 * A rate as an editable input value: '150.00' (as the input showed before the
 * API widened the column), '12.3456' when sub-cent, '' when absent. Never the
 * raw '150.0000' the API now sends.
 */
export function rateInputValue(v: number | string | undefined | null): string {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(rateDigits(n)) : '';
}

/**
 * A signed amount: `+$150.00`, `−$500.00`, `$0.00`; absent is `—`, as fmtCurrency.
 *
 * THE SIGN COMES FROM THE VALUE. The magnitude is fmtCurrency's and the sign is
 * the number's own, written as a true minus ahead of the symbol. Never prefix a
 * minus because of which column a figure sits in: the billing metrics send
 * `down` already negative (api#916), and a column-driven prefix on a value
 * that is already signed is how a −$500 reads as +$500.
 */
export function fmtSignedCurrency(v: number | string | undefined | null): string {
  const money = fmtCurrency(v === null || v === undefined || v === '' ? v : Math.abs(Number(v)));
  if (money === '—') return '—';
  const n = Number(v);
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}${money}`;
}

// ── Pay types ────────────────────────────────────────────────────────────────

/**
 * `payroll_entries.pay_type` / `pay_assignments.pay_type` — ONE vocabulary,
 * read by every surface that names a pay type.
 *
 * ── WHY THIS IS HERE AND NOT IN A SCREEN ───────────────────────────────────
 *
 * It used to be in five places, and they disagreed. Measured on main before
 * this change, for the same enum value:
 *
 *   percentage_single  →  "25% Single" | "Percentage (Single Tech)" | "% of Item"
 *   percentage_quad    →  "25% Quad"   | ABSENT ENTIRELY            | "% Quad"
 *                                                                   | "percentage_quad"
 *
 * Two of those divergences already reached a customer. #486 corrected
 * `percentage_triple` and `percentage_quad` falling through to a raw enum on 36
 * live pay stubs, and "0.0000" rendering as "0% Single" — both at ONE site,
 * because the others were not found. This module is that fix generalised: the
 * bug was never the switch statement, it was that there were five of them.
 *
 * ── THE TIER NAMES CHANGED, DELIBERATELY ───────────────────────────────────
 *
 * One vocabulary has to work in every context it appears in, and the tightest
 * of those is a bare `<select>` option with no percentage in front of it.
 * "Single" alone is not a pay type anyone can read, so the tier nouns carry
 * "Tech" — matching the API's own column names (`single_tech_percentage`, …)
 * and the longer form the Add-Employee modal already used. That composes in
 * both directions:
 *
 *   with a percentage   "25% Single Tech"     (pay stub, CSV, timesheet row)
 *   bare                "Single Tech"         (dropdown, assignment sub-label)
 *
 * The cost is that PayAssignmentScreen's "% of Item" is gone. It was the only
 * tier not named by its tier, and it named the wrong thing — every percentage
 * type is a percent of the item; what distinguishes them is the crew size.
 *
 * ── ABSENCE AND ZERO ARE THE SAME THING ────────────────────────────────────
 *
 * See `fmtPayPercentage`. This is the house convention (`fmtCurrency`,
 * `fmtQty`, `form-extraction`'s `formatConfidence`), and the reason it needs
 * restating here is that a percentage arrives from the API as the STRING
 * "0.0000", which is truthy. A `pct ? … : …` written against the number form
 * passes its test and ships the defect.
 */
export type PayType =
  | 'percentage_single'
  | 'percentage_dual'
  | 'percentage_triple'
  | 'percentage_quad'
  | 'day_rate'
  | 'hourly'
  | 'bonus'
  | 'stat_holiday'
  | 'other';

/**
 * Exhaustive on `PayType` — `Record<PayType, string>` is what makes adding a
 * tier to the union a compile error until it is named here. That is the guard
 * the five hand-rolled copies did not have, and it is why `percentage_quad`
 * could be missing from one of them for as long as it was.
 *
 * The ordered lists below name the subset each surface offers, and both read
 * their copy from HERE — so a dropdown cannot spell a type differently from the
 * row that renders it, which is exactly how the five copies drifted.
 */
export const PAY_TYPE_LABELS: Record<PayType, string> = {
  percentage_single: 'Single Tech',
  percentage_dual: 'Dual Tech',
  percentage_triple: 'Triple Tech',
  percentage_quad: 'Quad Tech',
  day_rate: 'Day Rate',
  hourly: 'Hourly',
  bonus: 'Bonus',
  stat_holiday: 'Stat Holiday',
  other: 'Other',
};

/** The percentage tiers, in crew-size order. */
export const PERCENTAGE_PAY_TYPES: readonly PayType[] = [
  'percentage_single',
  'percentage_dual',
  'percentage_triple',
  'percentage_quad',
];

/**
 * Every pay type a PAYROLL ENTRY can be given, in display order — the
 * Add-Employee modal's dropdown.
 */
export const PAYROLL_ENTRY_PAY_TYPES: readonly PayType[] = [
  'hourly',
  'day_rate',
  'percentage_single',
  'percentage_dual',
  'percentage_triple',
  'percentage_quad',
  'bonus',
  'stat_holiday',
  'other',
];

/**
 * Every pay type a PAY ASSIGNMENT on a ticket line item can be given.
 *
 * A narrower set than a payroll entry's, and narrower on purpose: an assignment
 * is always priced off one line item, so the flat-amount types have nothing to
 * be a percentage OF and no line item to attach to. This is a real difference
 * between two surfaces, not a sixth spelling — which is why it is a subset of
 * the one vocabulary rather than a list of its own strings.
 */
export const ASSIGNMENT_PAY_TYPES: readonly PayType[] = [
  ...PERCENTAGE_PAY_TYPES,
  'day_rate',
  'hourly',
];

/**
 * Whether this pay type is priced as a percentage of the line item.
 *
 * Replaces three separate four-way `||` chains in PayAssignmentScreen. Each one
 * had to be edited when `percentage_quad` shipped, and a chain that misses a
 * tier does not render wrong — it hides the percentage input entirely, so the
 * assignment saves with no percentage and earns $0.
 */
export function isPercentagePayType(payType: string | null | undefined): boolean {
  return (PERCENTAGE_PAY_TYPES as readonly string[]).includes(payType ?? '');
}

/**
 * A stored pay percentage as display copy. The value is a FRACTION (0.25), as
 * the API stores it; the output is a percentage ("25%").
 *
 * `absent` is what a caller renders when there is no percentage to show, and
 * the two registers in this codebase are both here on purpose:
 *
 *   `''`  — composed into a label, where the label carries the meaning on its
 *           own and a placeholder would be noise ("— Day Rate").
 *   `'—'` — a rate-sheet or summary CELL, which must occupy its slot.
 *
 * ZERO IS ABSENT, in both. A stored 0 cannot mean one thing: it is both "the
 * office deliberately zeroed this row" and "nothing has computed it yet"
 * (fieldiq-api #457, open). The earnings column beside it carries the meaning;
 * the display does not get to resolve an ambiguity the data has not resolved.
 *
 * `maxDecimals` is a display choice and nothing else. The pay stub rounds to
 * whole percent because that is what fits the cell; the pay-rate sheet keeps two
 * because a configured 11.6% is not 12%. Verified equivalent to the two
 * expressions this replaced across 0…1 at every 0.0001 step, all agreeing.
 */
export function fmtPayPercentage(
  v: number | string | null | undefined,
  opts?: { maxDecimals?: number; absent?: string },
): string {
  const { maxDecimals = 0, absent = '' } = opts ?? {};
  const n = Number(v);
  // `v === ''` would be NaN anyway; the guard is `> 0`, which excludes zero,
  // NaN, null (Number(null) is 0) and negatives in one predicate.
  if (!Number.isFinite(n) || n <= 0) return absent;
  const scale = 10 ** maxDecimals;
  return `${Math.round(n * 100 * scale) / scale}%`;
}

/**
 * The pay type as it appears to a human, with its percentage when it has one.
 *
 * Feeds the timesheet listing, the CSV export, the printed pay stub, the pay
 * assignment rows and the field worker's own pay screen. A wrong string here is
 * a wrong string on a document that leaves the building.
 *
 * An unknown type degrades to readable text rather than throwing — the API can
 * add a pay type before the frontend knows about it, and a raw enum with the
 * underscores taken out is a bad label, not a broken screen.
 */
export function fmtPayType(
  payType: string | null | undefined,
  pct?: string | number | null,
): string {
  if (!payType) return '—';
  const label = PAY_TYPE_LABELS[payType as PayType];
  if (!label) return payType.replace(/_/g, ' ');
  // The trailing space lives INSIDE the fragment, so an omitted percentage
  // leaves no leading space in front of the label.
  const p = isPercentagePayType(payType) ? fmtPayPercentage(pct) : '';
  return p ? `${p} ${label}` : label;
}
